import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Avatar,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  PlayArrow as TimeInIcon,
  Stop as TimeOutIcon,
  Person as PersonIcon,
  Today as TodayIcon,
  CalendarMonth as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  Coffee as BreakIcon,
  PlayCircle as ResumeIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { timeTrackingApi } from "../../../services/timetracking-api";
import configurationApiService from "../../../services/configuration-api";
import {
  EmployeeStatus,
  TimeEntry,
  getStatusColor,
  getStatusText,
  formatDuration, // formatDuration(hours: number) - for displaying hour totals
  formatBreakDuration,
  canStartBreak,
  canEndBreak,
} from "../../../types/timetracking";
import {
  formatTime,
  formatDate,
  utcToLocal,
} from "../../../services/dateTimeService";
import { useAuth } from "../../../contexts/AuthContext";
import { useWorkStatus } from "../../../contexts/WorkStatusContext";

const TimeInOut: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { refreshWorkStatus } = useWorkStatus();

  const [currentStatus, setCurrentStatus] = useState<EmployeeStatus | null>(
    null,
  );
  const [todayEntries, setTodayEntries] = useState<TimeEntry[]>([]);
  const [weekSummary, setWeekSummary] = useState<{
    totalHours: number;
    totalDays: number;
    averageHours: number;
  }>({ totalHours: 0, totalDays: 0, averageHours: 0 });
  const [monthSummary, setMonthSummary] = useState<{
    totalHours: number;
    totalDays: number;
    averageHours: number;
  }>({ totalHours: 0, totalDays: 0, averageHours: 0 });
  const [recentActivity, setRecentActivity] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Dialog states
  const [timeInDialogOpen, setTimeInDialogOpen] = useState(false);
  const [timeOutDialogOpen, setTimeOutDialogOpen] = useState(false);
  const [breakDialogOpen, setBreakDialogOpen] = useState(false);
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [expectedReturnTime, setExpectedReturnTime] = useState("");
  const [teleworkLink, setTeleworkLink] = useState<string | null>(null);

  // Get employee data from auth context
  const employeeId = user?.id || 1;
  const employeeName = user?.username || t("common.unknownUser");

  // Merge multiple sessions of the same day into one entry
  const mergeDailySessions = (entries: TimeEntry[]): TimeEntry[] => {
    const groupedByDate = entries.reduce(
      (acc, entry) => {
        const date = formatDate(entry.timeIn);
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(entry);
        return acc;
      },
      {} as Record<string, TimeEntry[]>,
    );

    return Object.values(groupedByDate).map((dayEntries) => {
      if (dayEntries.length === 1) {
        return dayEntries[0];
      }

      // Sort by timeIn to get earliest and latest
      const sorted = [...dayEntries].sort(
        (a, b) => new Date(a.timeIn).getTime() - new Date(b.timeIn).getTime(),
      );

      // Calculate total hours
      const totalHours = sorted.reduce(
        (sum, entry) => sum + entry.totalHours,
        0,
      );

      // Check if workday is completed (8h work + 45min lunch = 8.75h, or 4h with half-day leave)
      const hasHalfDayLeave = sorted.some((e) => e.hasHalfDayLeave);
      const isWorkdayCompleted =
        totalHours >= 8.75 || (hasHalfDayLeave && totalHours >= 4);

      // Use earliest timeIn and latest timeOut
      const mergedEntry: TimeEntry = {
        ...sorted[0],
        timeOut: sorted[sorted.length - 1].timeOut,
        totalHours,
        isWorkdayCompleted,
        hasHalfDayLeave,
        status: sorted[sorted.length - 1].status, // Use latest status
      };

      return mergedEntry;
    });
  };

  const loadCurrentStatus = async () => {
    try {
      setError(null);
      const dashboardData =
        await timeTrackingApi.getTimeTrackingDashboard(employeeId);
      setCurrentStatus(dashboardData.currentStatus);
      setTodayEntries(dashboardData.todayEntries);
      setWeekSummary(dashboardData.weekSummary);
      setMonthSummary(dashboardData.monthSummary);
      // Merge sessions for recent activity display
      setRecentActivity(mergeDailySessions(dashboardData.recentActivity));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("error.failedToLoadDashboardData"),
      );
    } finally {
      setLoading(false);
    }
  };

  // Calculate today's total hours including current active session (excluding break time)
  const calculateTodaysTotalHours = (): number => {
    if (!currentStatus) return 0;

    // Sum up completed sessions (totalHours should already exclude break time)
    const completedHours = todayEntries.reduce(
      (sum, entry) => sum + entry.totalHours,
      0,
    );

    // Add current active session if employee is timed in
    if (currentStatus.currentStatus === "IN" && currentStatus.lastTimeIn) {
      try {
        const startTime = utcToLocal(currentStatus.lastTimeIn);
        const currentTime = new Date();
        const durationMs = currentTime.getTime() - startTime.getTime();
        let currentSessionHours = Math.max(0, durationMs / (1000 * 60 * 60));

        // Subtract break time from current session
        if (
          currentStatus.totalBreakMinutes &&
          currentStatus.totalBreakMinutes > 0
        ) {
          const breakHours = currentStatus.totalBreakMinutes / 60;
          currentSessionHours = Math.max(0, currentSessionHours - breakHours);
        }

        return completedHours + currentSessionHours;
      } catch {
        return completedHours;
      }
    }

    return completedHours;
  };

  const handleTimeIn = async () => {
    setActionLoading(true);
    try {
      await timeTrackingApi.timeIn({
        employeeId,
        employeeName: undefined, // Let backend get real name from Employee table
        notes: notes.trim().replace(/\r?\n/g, "<br>") || undefined,
      });

      setSuccessMessage(t("timetracking.messages.timeInSuccess"));
      setTimeInDialogOpen(false);
      setNotes("");
      await loadCurrentStatus(); // Refresh status and today's entries

      // Trigger immediate refresh of work status in Header
      await refreshWorkStatus();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("timetracking.errors.failedToTimeIn"),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleTimeInWithTelework = async () => {
    setActionLoading(true);
    try {
      await timeTrackingApi.timeIn({
        employeeId,
        employeeName: undefined, // Let backend get real name from Employee table
        notes: notes.trim().replace(/\r?\n/g, "<br>") || undefined,
      });

      setSuccessMessage(t("timetracking.messages.timeInSuccess"));
      setTimeInDialogOpen(false);
      setNotes("");
      await loadCurrentStatus(); // Refresh status and today's entries

      // Trigger immediate refresh of work status in Header
      await refreshWorkStatus();

      // Open telework link in a new tab
      if (teleworkLink && teleworkLink.trim()) {
        window.open(teleworkLink, "_blank");
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("timetracking.errors.failedToTimeIn"),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleTimeOut = async () => {
    setActionLoading(true);
    try {
      await timeTrackingApi.timeOut({
        employeeId,
        notes: notes.trim().replace(/\r?\n/g, "<br>") || undefined,
      });

      setSuccessMessage(t("timetracking.messages.timeOutSuccess"));
      setTimeOutDialogOpen(false);
      setNotes("");
      await loadCurrentStatus(); // Refresh status and today's entries

      // Trigger immediate refresh of work status in Header
      await refreshWorkStatus();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("timetracking.errors.failedToTimeOut"),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleStartBreak = async () => {
    setActionLoading(true);
    try {
      // Build notes with expected return time appended
      const userNotes = notes.trim().replace(/\r?\n/g, "<br>");
      const returnTimeNote = `(Expected Return Time: ${expectedReturnTime.trim()})`;
      const combinedNotes = userNotes
        ? `${userNotes}<br>${returnTimeNote}`
        : returnTimeNote;

      await timeTrackingApi.startBreak({
        employeeId,
        notes: combinedNotes,
      });

      setSuccessMessage(t("timetracking.messages.breakStartSuccess"));
      setBreakDialogOpen(false);
      setNotes("");
      setExpectedReturnTime("");
      await loadCurrentStatus();
      await refreshWorkStatus();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("timetracking.errors.failedToStartBreak"),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setActionLoading(true);
    try {
      await timeTrackingApi.endBreak({
        employeeId,
      });

      setSuccessMessage(t("timetracking.messages.breakEndSuccess"));
      setResumeDialogOpen(false);
      await loadCurrentStatus();
      await refreshWorkStatus();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("timetracking.errors.failedToEndBreak"),
      );
    } finally {
      setActionLoading(false);
    }
  };

  const openTimeInDialog = () => {
    setNotes("");
    setTimeInDialogOpen(true);
  };

  const openTimeOutDialog = () => {
    setNotes("");
    setTimeOutDialogOpen(true);
  };

  const openBreakDialog = () => {
    setNotes("");
    setExpectedReturnTime("");
    setBreakDialogOpen(true);
  };

  const openResumeDialog = () => {
    setResumeDialogOpen(true);
  };

  const closeDialogs = () => {
    setTimeInDialogOpen(false);
    setTimeOutDialogOpen(false);
    setBreakDialogOpen(false);
    setResumeDialogOpen(false);
    setNotes("");
    setExpectedReturnTime("");
  };

  // Preset messages for Time In
  const timeInPresetMessages = [
    "Working at the office",
    "Working from home",
    "Fieldwork / On the field",
    "Business trip",
    "おはようございます",
  ];

  // Preset messages for Time Out
  const timeOutPresetMessages = [
    "Signing off, see you tomorrow",
    "TGIF, See you next week",
    "Done for today",
    "おつかれさまです",
    "お先に失礼します",
  ];

  // Preset messages for Expected Return Time
  const expectedReturnTimePresets = ["1 hour", "2 hours"];

  // Preset messages for Break Notes
  const breakNotesPresets = [
    "Personal Errand",
    "Emergency",
    "Health Break",
    "Rest Break",
  ];

  // Handle preset message click
  const handlePresetMessageClick = (message: string) => {
    setNotes(message);
  };

  // Handle expected return time preset click
  const handleExpectedReturnTimePresetClick = (message: string) => {
    setExpectedReturnTime(message);
  };

  // Handle break notes preset click
  const handleBreakNotesPresetClick = (message: string) => {
    setNotes(message);
  };

  useEffect(() => {
    loadCurrentStatus();

    // Load telework link
    const loadTeleworkLink = async () => {
      try {
        const link = await configurationApiService.getTeleworkLink();
        if (link && link.trim()) {
          setTeleworkLink(link);
        }
      } catch {
        // Telework link is optional, don't show error
      }
    };
    loadTeleworkLink();

    // Auto-refresh status every 30 seconds
    const interval = setInterval(loadCurrentStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !currentStatus) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={loadCurrentStatus}>
            {t("common.retry")}
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  const isWorking = currentStatus?.currentStatus === "IN";
  const isOnBreak = currentStatus?.isOnBreak === true;
  const canTimeInNow = !isWorking;
  const canTimeOutNow = isWorking && !isOnBreak;
  const canBreakNow = currentStatus ? canStartBreak(currentStatus) : false;
  const canResumeNow = currentStatus ? canEndBreak(currentStatus) : false;

  return (
    <Box sx={{ pt: 0, pr: 2, pb: 2, pl: 2 }}>
      <Grid container spacing={3}>
        {/* Action Buttons Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={2} sx={{ height: "100%" }}>
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                {t("timetracking.dashboard.attendance")}
              </Typography>

              <Box display="flex" flexDirection="column" gap={2} mt={3}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<TimeInIcon />}
                  onClick={openTimeInDialog}
                  disabled={!canTimeInNow || actionLoading}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {t("timetracking.actions.timeIn")}
                </Button>

                {/* Break/Resume Button - only show when timed in */}
                {isWorking && (
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={isOnBreak ? <ResumeIcon /> : <BreakIcon />}
                    onClick={isOnBreak ? openResumeDialog : openBreakDialog}
                    disabled={actionLoading}
                    fullWidth
                    sx={{
                      py: 2,
                      background: (theme) => theme.palette.info.main,
                      "&:hover": {
                        background: (theme) => theme.palette.info.dark,
                      },
                    }}
                  >
                    {isOnBreak
                      ? t("timetracking.actions.resume")
                      : t("timetracking.actions.break")}
                  </Button>
                )}

                <Button
                  variant="contained"
                  color="secondary"
                  size="large"
                  startIcon={<TimeOutIcon />}
                  onClick={openTimeOutDialog}
                  disabled={!canTimeOutNow || actionLoading}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  {t("timetracking.actions.timeOut")}
                </Button>

                {actionLoading && (
                  <Box display="flex" justifyContent="center" mt={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>

              {/* <Box mt={3}>
                <Typography variant="body2" color="text.secondary">
                  {canTimeIn && t("timetracking.messages.notTimedIn")}
                  {canTimeOut && t("timetracking.messages.alreadyTimedIn")}
                </Typography>
              </Box> */}

              {teleworkLink && (
                <Box
                  p={1}
                  mt={1}
                  sx={{ bgcolor: "info.lighter", borderRadius: 1 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1.5, whiteSpace: "pre-line" }}
                  >
                    {t("timetracking.messages.teleworkLinkInfo")}
                  </Typography>
                  <Typography
                    component="a"
                    href={teleworkLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="body2"
                    sx={{
                      color: "primary.main",
                      textDecoration: "none",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-block",
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {t("timetracking.messages.openTeleworkLink")}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Current Status Card */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card elevation={2} sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={3}>
                <Avatar
                  sx={{
                    bgcolor:
                      getStatusColor(
                        currentStatus?.currentStatus || null,
                        currentStatus?.isWorkdayCompleted,
                        currentStatus?.isOnBreak,
                      ) === "success"
                        ? "success.main"
                        : getStatusColor(
                              currentStatus?.currentStatus || null,
                              currentStatus?.isWorkdayCompleted,
                              currentStatus?.isOnBreak,
                            ) === "primary"
                          ? "primary.main"
                          : getStatusColor(
                                currentStatus?.currentStatus || null,
                                currentStatus?.isWorkdayCompleted,
                                currentStatus?.isOnBreak,
                              ) === "error"
                            ? "error.main"
                            : getStatusColor(
                                  currentStatus?.currentStatus || null,
                                  currentStatus?.isWorkdayCompleted,
                                  currentStatus?.isOnBreak,
                                ) === "info"
                              ? "info.main"
                              : "grey.500",
                    mr: 2,
                    width: 56,
                    height: 56,
                  }}
                >
                  {isOnBreak ? <BreakIcon /> : <PersonIcon />}
                </Avatar>
                <Box>
                  <Typography variant="h6" component="div">
                    {currentStatus?.employeeName || employeeName}
                  </Typography>
                  <Chip
                    label={t(
                      getStatusText(
                        currentStatus?.currentStatus || null,
                        currentStatus?.isWorkdayCompleted,
                        currentStatus?.isOnBreak,
                      ),
                    )}
                    color={
                      getStatusColor(
                        currentStatus?.currentStatus || null,
                        currentStatus?.isWorkdayCompleted,
                        currentStatus?.isOnBreak,
                      ) as any
                    }
                    size="medium"
                  />
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {isOnBreak
                    ? t("timetracking.labels.breakDuration")
                    : t("timetracking.currentStatus")}
                </Typography>
                <Typography
                  variant="h4"
                  color={
                    isOnBreak
                      ? "info.main"
                      : isWorking
                        ? "success.main"
                        : "text.secondary"
                  }
                >
                  {currentStatus?.currentSessionDuration || "0h 0m"}
                </Typography>
              </Box>

              {/* Show total break time if any */}
              {currentStatus?.totalBreakMinutes &&
                currentStatus.totalBreakMinutes > 0 && (
                  <Box mb={2}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("timetracking.labels.totalBreakTime")}
                    </Typography>
                    <Typography variant="body1" color="info.main">
                      {formatBreakDuration(currentStatus.totalBreakMinutes)}
                    </Typography>
                  </Box>
                )}

              {currentStatus?.lastTimeIn && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {isOnBreak
                      ? t("timetracking.messages.onBreakSince", {
                          time: currentStatus.currentBreakStart
                            ? formatTime(currentStatus.currentBreakStart)
                            : "",
                        })
                      : isWorking
                        ? `${t("timetracking.dashboard.workingSince")} ${formatTime(currentStatus.lastTimeIn)}`
                        : `${t("timetracking.dashboard.lastWorked")} ${formatTime(currentStatus.lastTimeOut || currentStatus.lastTimeIn)}`}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Summary */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                  <TodayIcon />
                </Avatar>
                <Typography variant="h6" component="div">
                  {t("timetracking.todayHours")}
                </Typography>
              </Box>

              <Typography variant="h3" color="primary.main" gutterBottom>
                {formatDuration(calculateTodaysTotalHours())}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {todayEntries.length}{" "}
                {todayEntries.length === 1
                  ? t("timetracking.dashboard.session")
                  : t("timetracking.dashboard.sessions")}{" "}
                {t("timetracking.dashboard.today")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Week Summary */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: "secondary.main", mr: 2 }}>
                  <CalendarIcon />
                </Avatar>
                <Typography variant="h6" component="div">
                  {t("timetracking.weekHours")}
                </Typography>
              </Box>

              <Typography variant="h3" color="secondary.main" gutterBottom>
                {formatDuration(weekSummary.totalHours)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {weekSummary.totalDays}{" "}
                {weekSummary.totalDays === 1
                  ? t("timetracking.dashboard.day")
                  : t("timetracking.dashboard.days")}{" "}
                {t("timetracking.dashboard.worked")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Month Summary */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card elevation={2}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: "info.main", mr: 2 }}>
                  <TrendingUpIcon />
                </Avatar>
                <Typography variant="h6" component="div">
                  {t("timetracking.monthHours")}
                </Typography>
              </Box>

              <Typography variant="h3" color="info.main" gutterBottom>
                {formatDuration(monthSummary.totalHours)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {monthSummary.totalDays}{" "}
                {monthSummary.totalDays === 1
                  ? t("timetracking.dashboard.day")
                  : t("timetracking.dashboard.days")}{" "}
                {t("timetracking.dashboard.worked")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12 }}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" component="div" gutterBottom>
                {t("timetracking.dashboard.recentActivity")}
              </Typography>

              {recentActivity.length === 0 ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ py: 2 }}
                >
                  {t("timetracking.messages.noEntriesFound")}
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                  {recentActivity.slice(0, 5).map((entry, index) => (
                    <Box
                      key={entry.id}
                      sx={{
                        py: 1,
                        borderBottom: index < 4 ? "1px solid #eee" : "none",
                      }}
                    >
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {formatDate(entry.timeIn)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatTime(entry.timeIn)} -{" "}
                            {entry.timeOut
                              ? formatTime(entry.timeOut)
                              : t("timetracking.teamStatus.currentlyWorking")}
                          </Typography>
                        </Box>
                        <Box textAlign="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatDuration(entry.totalHours)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {entry.isWorkdayCompleted
                              ? t("timetracking.status.WORKDAY_COMPLETED")
                              : entry.status === "IN"
                                ? t("timetracking.status.IN")
                                : t("timetracking.status.OUT")}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Time In Dialog */}
      <Dialog
        open={timeInDialogOpen}
        onClose={closeDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("timetracking.actions.timeIn")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("timetracking.messages.enterNotes")}
          </Typography>

          {/* Preset Messages */}
          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {timeInPresetMessages.map((message, index) => (
              <Chip
                key={index}
                label={message}
                onClick={() => handlePresetMessageClick(message)}
                variant="outlined"
                size="small"
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgba(25, 118, 210, 0.08)",
                    borderColor: "primary.main",
                  },
                }}
              />
            ))}
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label={t("timetracking.labels.notes")}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("timetracking.labels.optionalNotes")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs} disabled={actionLoading}>
            {t("common.cancel")}
          </Button>
          {teleworkLink && (
            <Button
              onClick={handleTimeInWithTelework}
              variant="contained"
              color="primary"
              disabled={actionLoading}
              startIcon={
                actionLoading ? <CircularProgress size={16} /> : <TimeInIcon />
              }
            >
              {t("timetracking.actions.timeInWithTelework")}
            </Button>
          )}
          <Button
            onClick={handleTimeIn}
            variant="contained"
            disabled={actionLoading}
            startIcon={
              actionLoading ? <CircularProgress size={16} /> : <TimeInIcon />
            }
          >
            {t("timetracking.actions.timeIn")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Time Out Dialog */}
      <Dialog
        open={timeOutDialogOpen}
        onClose={closeDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("timetracking.actions.timeOut")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("timetracking.messages.enterNotes")}
          </Typography>

          {/* Preset Messages */}
          <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {timeOutPresetMessages.map((message, index) => (
              <Chip
                key={index}
                label={message}
                onClick={() => handlePresetMessageClick(message)}
                variant="outlined"
                size="small"
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "rgba(237, 108, 2, 0.08)",
                    borderColor: "secondary.main",
                  },
                }}
              />
            ))}
          </Box>

          <TextField
            autoFocus
            margin="dense"
            label={t("timetracking.labels.notes")}
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("timetracking.labels.optionalNotes")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs} disabled={actionLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleTimeOut}
            variant="contained"
            color="secondary"
            disabled={actionLoading}
            startIcon={
              actionLoading ? <CircularProgress size={16} /> : <TimeOutIcon />
            }
          >
            {t("timetracking.actions.timeOut")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Break Dialog */}
      <Dialog
        open={breakDialogOpen}
        onClose={closeDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("timetracking.actions.break")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("timetracking.messages.breakConfirmation")}
          </Typography>

          {/* Expected Return Time Section */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {t("timetracking.labels.expectedReturnTime")} *
          </Typography>
          <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {expectedReturnTimePresets.map((message, index) => (
              <Chip
                key={index}
                label={message}
                onClick={() => handleExpectedReturnTimePresetClick(message)}
                variant={expectedReturnTime === message ? "filled" : "outlined"}
                color={expectedReturnTime === message ? "info" : "default"}
                size="small"
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              />
            ))}
          </Box>
          <TextField
            autoFocus
            margin="dense"
            label={t("timetracking.labels.expectedReturnTime")}
            fullWidth
            variant="outlined"
            value={expectedReturnTime}
            onChange={(e) => setExpectedReturnTime(e.target.value)}
            required
            size="small"
            helperText={t("timetracking.labels.expectedReturnTimeHelper")}
          />

          {/* Break Notes Section */}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            {t("timetracking.labels.breakNotes")} *
          </Typography>
          <Box sx={{ mb: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {breakNotesPresets.map((message, index) => (
              <Chip
                key={index}
                label={message}
                onClick={() => handleBreakNotesPresetClick(message)}
                variant={notes === message ? "filled" : "outlined"}
                color={notes === message ? "info" : "default"}
                size="small"
                sx={{
                  cursor: "pointer",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              />
            ))}
          </Box>
          <TextField
            margin="dense"
            label={t("timetracking.labels.breakNotes")}
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("timetracking.labels.breakNotesPlaceholder")}
            required
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs} disabled={actionLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleStartBreak}
            variant="contained"
            disabled={
              actionLoading || !expectedReturnTime.trim() || !notes.trim()
            }
            startIcon={
              actionLoading ? <CircularProgress size={16} /> : <BreakIcon />
            }
            sx={{
              background: (theme) => theme.palette.info.main,
              "&:hover": {
                background: (theme) => theme.palette.info.dark,
              },
            }}
          >
            {t("timetracking.actions.startBreak")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resume Dialog */}
      <Dialog
        open={resumeDialogOpen}
        onClose={closeDialogs}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("timetracking.actions.resume")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {t("timetracking.messages.resumeConfirmation")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialogs} disabled={actionLoading}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleEndBreak}
            variant="contained"
            disabled={actionLoading}
            startIcon={
              actionLoading ? <CircularProgress size={16} /> : <ResumeIcon />
            }
            sx={{
              background: (theme) => theme.palette.info.main,
              "&:hover": {
                background: (theme) => theme.palette.info.dark,
              },
            }}
          >
            {t("timetracking.actions.resumeWork")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TimeInOut;
